import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { openPopup } from 'modules/popup/actions';
import { OTHER_DISEASE } from 'utils/constants/disease';
import { POPUP } from 'utils/constants/popup';
import { usePathNameChanged } from 'utils/hooks/usePathNameChanged';
import { useAuth } from 'utils/hooks/useAuth';
import { selectIsAnyPopupShown } from 'modules/popup/popup.selectors';
import app from 'new-ui/app';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { usePreliminaryAuthUser } from 'utils/hooks/usePreliminaryAuthUser';
import { usePageHeader } from './PageHeaderContext';
import { QuestionnairePageHeaderView } from './QuestionnairePageHeaderView/QuestionnairePageHeaderView';
import { PatientMainPageHeaderView } from './PatientMainPageHeaderView/PatientMainPageHeaderView';
import { HCPPageHeaderView } from './HCPPageHeaderView/HCPPageHeaderView';
import { CustomPageHeaderView } from './CustomPageHeaderView/CustomPageHeaderView';
import { DefaultPageHeaderView } from './DefaultPageHeaderView/DefaultPageHeaderView';
import { TAB_IDS, VERSIONS } from './constants';
import { getActiveTab } from './pageHeader.helpers';

const VIEW_BY_VERSION = {
  [VERSIONS.DEFAULT]: DefaultPageHeaderView,
  [VERSIONS.QUESTIONNAIRE]: QuestionnairePageHeaderView,
  [VERSIONS.PATIENT_MAIN]: PatientMainPageHeaderView,
  [VERSIONS.HCP]: HCPPageHeaderView,
  [VERSIONS.CUSTOM]: CustomPageHeaderView,
};

const getLogoClickHandler = ({
  dispatch, isAuthenticated, customerRef, customLogoClickHandler,
}) => () => {
  if (customLogoClickHandler?.() === false) {
    return;
  }
  if (
    app.isCurrentPage(ROUTE_NAME.RESULTS) || app.isCurrentPage(ROUTE_NAME.FINANCE)
    || app.isCurrentPage(ROUTE_NAME.CHAT)
  ) {
    dispatch(push(ROUTE_PATH[ROUTE_NAME.FEED]));
    return;
  }
  if (isAuthenticated) {
    dispatch(push(ROUTE_PATH[customerRef ? ROUTE_NAME.FEED : ROUTE_NAME.RESULTS]));
  } else {
    dispatch(push(ROUTE_PATH[ROUTE_NAME.HOME]));
  }
};

const adjustHeaderSettings = (rawValue, {
  isAuthenticated, isAuthPending, customerRef, condition, providerCode,
}) => {
  const {
    tabIds,
    isLoginButtonShown,
    isLogoShown,
    isProfileButtonShown,
    isUploadButtonShown,
    isBurgerMenuShown,
    ...restSettings
  } = rawValue;
  const finalTabIds = (tabIds ?? []).filter((i) => (
    (i !== TAB_IDS.TREATMENT_OPTIONS
    || (i === TAB_IDS.TREATMENT_OPTIONS && !customerRef && condition !== OTHER_DISEASE))
    && (i !== TAB_IDS.SPEAK_WITH_TINA || isAuthenticated)
  ));
  return {
    ...restSettings,
    isLogoShown: !providerCode && isLogoShown,
    tabIds: finalTabIds,
    isLoginButtonShown: !isAuthenticated && !isAuthPending && isLoginButtonShown,
    isProfileButtonShown: isAuthenticated && isProfileButtonShown,
    isUploadButtonShown: isAuthenticated && isUploadButtonShown,
    isBurgerMenuShown: isAuthenticated && isBurgerMenuShown,
  };
};

export const PageHeader = () => {
  const dispatch = useDispatch();
  const mobileMenuClosedActionRef = useRef(null);
  const { isAuthenticated, isAuthPending } = useAuth();
  const isAnyPopupShown = useSelector((state) => selectIsAnyPopupShown(state));
  const { condition, customerRef } = usePreliminaryAuthUser() ?? {};
  const { providerSpecific: { code: providerCode } = {} } = useSelector((state) => state.appConfiguration);

  const {
    onLogoClick,
    headerContent, // complete replacement of the header tag content
    headerSettings: rawHeaderSettings,
    headerRightSlot,
  } = usePageHeader();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isAnyPopupShown || !isMobileMenuOpen) {
      return;
    }
    setIsMobileMenuOpen(false);
  }, [isAnyPopupShown, isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  usePathNameChanged(useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []));

  useEffect(() => {
    if (isMobileMenuOpen || !mobileMenuClosedActionRef.current) {
      return;
    }
    mobileMenuClosedActionRef.current(dispatch);
    mobileMenuClosedActionRef.current = null;
  }, [dispatch, isMobileMenuOpen]);

  const headerSettings = adjustHeaderSettings(rawHeaderSettings, {
    isAuthenticated, isAuthPending, customerRef, condition, providerCode,
  });
  const {
    isShown = true,
    tabIds,
    version,
  } = headerSettings;

  const handleMobileMenuItemClick = (action) => {
    if (isMobileMenuOpen) {
      mobileMenuClosedActionRef.current = action;
      setIsMobileMenuOpen(false);
    } else {
      action(dispatch);
    }
  };

  if (!isShown || !version) {
    return null;
  }

  const View = VIEW_BY_VERSION[version];

  if (!View) {
    return null;
  }

  return (
    <View
      {...headerSettings}
      activeTab={getActiveTab(tabIds)}
      isAuthenticated={isAuthenticated}
      isMobileMenuOpen={isMobileMenuOpen}
      isProviderMode={!!providerCode}
      onLogoClick={getLogoClickHandler({
        isAuthenticated, customerRef, dispatch, customLogoClickHandler: onLogoClick,
      })}
      onProfileButtonClick={() => {
        dispatch(openPopup({ type: POPUP.MEDICAL_PROFILE }));
      }}
      onHelpWithTinaClick={() => {
        dispatch(push(ROUTE_PATH[ROUTE_NAME.CHAT]));
      }}
      toggleMobileMenu={() => setIsMobileMenuOpen((v) => !v)}
      onMobileMenuItemClick={handleMobileMenuItemClick}
      headerContent={headerContent}
      headerRightSlot={headerRightSlot}
      userCondition={condition}
    />
  );
};
